import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export const arr = (v) => (Array.isArray(v) ? v : []);
export const nameOf = (s) => (typeof s === "string" ? s : s?.name || "");
export const uniq = (list) => {
  const seen = new Set();
  return list.map((x) => String(x || "").trim()).filter((x) => x && !seen.has(x.toLowerCase()) && seen.add(x.toLowerCase()));
};

const box = "rounded-lg border border-input bg-background/60 transition-[border-color,box-shadow] focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20";

export function Field({ label, hint, children, className }) {
  return (
    <div className={cn("space-y-1.5 min-w-0", className)}>
      <div className="text-xs font-medium text-muted-foreground">{label}{hint && <span className="ml-1.5 font-normal text-muted-foreground/70">· {hint}</span>}</div>
      {children}
    </div>
  );
}

export function TextField({ value, onChange, testId, className, ...rest }) {
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={cn("h-10 bg-background/60 focus-visible:ring-primary/30", className)} {...rest} />;
}

export function AutoTextarea({ value, onChange, className, ref, testId, ...rest }) {
  const inner = useRef(null);
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  const setRef = (el) => { inner.current = el; if (typeof ref === "function") ref(el); };
  return (
    <textarea ref={setRef} rows={1} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className={cn("block w-full resize-none overflow-hidden px-3 py-2 text-sm leading-6 outline-none placeholder:text-muted-foreground/60", box, className)} {...rest} />
  );
}

export function ChipInput({ value = [], onChange, placeholder = "Type and press Enter", renderChip, testId }) {
  const [draft, setDraft] = useState("");
  const add = (raw) => {
    const parts = raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (parts.length) onChange(uniq([...value, ...parts]));
    setDraft("");
  };
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); }
  };
  const onPaste = (e) => {
    const t = e.clipboardData.getData("text");
    if (/[,\n]/.test(t)) { e.preventDefault(); add(draft + t); }
  };
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5 p-2", box)} data-testid={testId}>
      {value.map((v, i) => (
        <span key={`${v}-${i}`} className="inline-flex max-w-full items-center gap-1 rounded-full border border-primary/25 bg-primary/10 py-0.5 pl-2.5 pr-1 text-[13px]" data-testid={`${testId}-chip-${i}`}>
          <span className="truncate">{renderChip ? renderChip(v) : v}</span>
          <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} aria-label={`Remove ${v}`} data-testid={`${testId}-remove-${i}`}
            className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-rose-500/15 hover:text-rose-400"><X className="h-3 w-3" /></button>
        </span>
      ))}
      <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onPaste={onPaste} onBlur={() => add(draft)}
        placeholder={value.length ? "Add more…" : placeholder} data-testid={`${testId}-input`}
        className="min-w-[9rem] flex-1 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-muted-foreground/60" />
    </div>
  );
}

export function BulletList({ value = [], onChange, placeholder, addLabel = "Add a point", testId }) {
  const refs = useRef([]);
  const [focus, setFocus] = useState(null);
  useEffect(() => { if (focus != null) { refs.current[focus]?.focus(); setFocus(null); } }, [focus]);
  const setAt = (i, v) => onChange(value.map((x, idx) => (idx === i ? v : x)));
  const insertAfter = (i) => { const next = [...value]; next.splice(i + 1, 0, ""); onChange(next); setFocus(i + 1); };
  const removeAt = (i) => { onChange(value.filter((_, idx) => idx !== i)); setFocus(i > 0 ? i - 1 : null); };
  const onKey = (i) => (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); insertAfter(i); }
    else if (e.key === "Backspace" && !value[i]) { e.preventDefault(); removeAt(i); }
  };
  return (
    <div className="space-y-1.5" data-testid={testId}>
      {value.map((v, i) => (
        <div key={i} className="group flex items-start gap-2">
          <span className="mt-[17px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
          <AutoTextarea ref={(el) => { refs.current[i] = el; }} value={String(v)} onChange={(t) => setAt(i, t)} onKeyDown={onKey(i)} placeholder={placeholder} testId={`${testId}-${i}`} />
          <button type="button" onClick={() => removeAt(i)} aria-label="Remove point" data-testid={`${testId}-remove-${i}`}
            className="mt-2 rounded-md p-1.5 text-muted-foreground opacity-60 transition-[opacity,color] hover:text-rose-400 group-hover:opacity-100 group-focus-within:opacity-100"><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <button type="button" onClick={() => insertAfter(value.length - 1)} data-testid={`${testId}-add`}
        className="ml-3.5 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"><Plus className="h-3.5 w-3.5" />{addLabel}</button>
    </div>
  );
}
